import { useCallback, useMemo, useState } from "react";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";
import { captureCardTemplate, generateOrgChart } from "../services/orgChartBuilder";
import { parseOrgCsv } from "../services/orgCsv";
import type { OrgCardTemplate, OrgInsertMode, OrgLayout, OrgPerson } from "../../../types";

/** Default card colour (WL deep blue), hex WITHOUT "#". */
const DEFAULT_CARD_COLOR = "003865";

/**
 * State + actions for the Org Chart feature (ported from the Deckorate
 * prototype). Owns the people list, layout/colour choices, per-person photos, a
 * captured card template, the insert-mode toggle, CSV import, and the generate
 * runner, which wraps the Office.js builder with busy/status/error handling and
 * emits the shared Deckorate telemetry (usage on success, `error.connector.failure`
 * on failure) — the same contract as `useFormatTools`.
 */
export function useOrgChart() {
  const [people, setPeople] = useState<OrgPerson[]>([]);
  const [layout, setLayout] = useState<OrgLayout>("top-down");
  const [cardColor, setCardColor] = useState(DEFAULT_CARD_COLOR);
  const [showPhotoHead, setShowPhotoHead] = useState(false);
  const [insertMode, setInsertMode] = useState<OrgInsertMode>("current");
  const [template, setTemplate] = useState<OrgCardTemplate | null>(null);

  const [busy, setBusy] = useState(false);
  const [capturingTemplate, setCapturingTemplate] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Monotonic id source, independent of array length so removals never collide.
  const nextId = useMemo(
    () => people.reduce((max, p) => Math.max(max, p.id), 0) + 1,
    [people],
  );

  /** At least one person has a photo — drives the insert-mode picker. */
  const hasPhotos = useMemo(() => people.some((p) => !!p.photoDataUrl), [people]);

  const addPerson = useCallback(
    (name: string, title: string, parentId: number | null) => {
      const trimmed = name.trim();
      if (!trimmed) {
        setError("Please enter a name.");
        return false;
      }
      setError(null);
      setPeople((prev) => [
        ...prev,
        {
          id: prev.reduce((max, p) => Math.max(max, p.id), 0) + 1,
          name: trimmed,
          title: title.trim(),
          parentId,
        },
      ]);
      return true;
    },
    [],
  );

  /** Remove a person; anyone reporting to them is re-parented to *their*
   * manager so the tree stays connected. */
  const removePerson = useCallback((id: number) => {
    setPeople((prev) => {
      const removed = prev.find((p) => p.id === id);
      const grandparent = removed ? removed.parentId : null;
      return prev
        .filter((p) => p.id !== id)
        .map((p) => (p.parentId === id ? { ...p, parentId: grandparent } : p));
    });
  }, []);

  const clearPeople = useCallback(() => {
    setPeople([]);
    setStatus(null);
    setError(null);
  }, []);

  /** Surface an input/read error to the panel (e.g. an unreadable file). */
  const reportError = useCallback((message: string) => {
    setError(message);
  }, []);

  /** Attach (or clear, with `null`) a photo `data:` URL for one person. */
  const setPersonPhoto = useCallback((id: number, photoDataUrl: string | null) => {
    setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, photoDataUrl } : p)));
  }, []);

  /** Replace the whole list from parsed CSV text. */
  const importCsv = useCallback((text: string): boolean => {
    try {
      const imported = parseOrgCsv(text);
      setPeople(imported);
      setError(null);
      setStatus(`Imported ${imported.length} ${imported.length === 1 ? "person" : "people"} from CSV.`);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read that CSV file.");
      return false;
    }
  }, []);

  /** Capture the selected shape's style as the card template. */
  const captureTemplate = useCallback(async () => {
    setError(null);
    setCapturingTemplate(true);
    try {
      const captured = await captureCardTemplate();
      setTemplate(captured);
      setStatus("Card template captured from the selected shape.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read the selected shape.");
    } finally {
      setCapturingTemplate(false);
    }
  }, []);

  const clearTemplate = useCallback(() => setTemplate(null), []);

  const generate = useCallback(async () => {
    setError(null);
    setStatus(null);
    setBusy(true);
    try {
      const result = await generateOrgChart(people, {
        layout,
        cardColor,
        showPhotoHead,
        insertMode,
        template,
      });
      setStatus(
        `Org chart created — ${result.cardCount} card${result.cardCount !== 1 ? "s" : ""}.`,
      );
      logEvent("connector.action.invoke", "usage", { connectorSystemName: "deckorate" });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Org chart failed";
      setError(msg);
      logError(
        "error.connector.failure",
        {
          type: e instanceof Error ? e.name : "DeckorateOrgChartError",
          scrubbedMessage: scrubMessage(msg),
        },
        { connectorSystemName: "deckorate" },
      );
    } finally {
      setBusy(false);
    }
  }, [people, layout, cardColor, showPhotoHead, insertMode, template]);

  return {
    people,
    layout,
    cardColor,
    showPhotoHead,
    insertMode,
    template,
    hasPhotos,
    nextId,
    busy,
    capturingTemplate,
    status,
    error,
    setLayout,
    setCardColor,
    setShowPhotoHead,
    setInsertMode,
    addPerson,
    removePerson,
    clearPeople,
    reportError,
    setPersonPhoto,
    importCsv,
    captureTemplate,
    clearTemplate,
    generate,
  };
}

export type OrgChartController = ReturnType<typeof useOrgChart>;
